import {
	ApolloClient,
	ApolloLink,
	ApolloProvider as BaseApolloProvider,
	InMemoryCache,
	NormalizedCacheObject,
	concat,
} from '@apollo/client';
import { PropsWithChildren, useRef } from 'react';

import { useHttpLink, useWorkspace } from '@treams/treams-hooks';

import { mergeCompetencyAppraisals } from './appraisalCache';

export const ApolloProvider = ({ children }: PropsWithChildren) => {
	const workspace = useWorkspace();

	const client = useRef<ApolloClient<NormalizedCacheObject>>(
		null as unknown as ApolloClient<NormalizedCacheObject>,
	);

	// Expects an apolloClient but gets null?
	// hook also doesn't seem to have null checks
	const httpLink = useHttpLink();

	if (!client.current) {
		const authMiddleware = new ApolloLink((operation, forward) => {
			operation.setContext({
				headers: {
					...operation.getContext().headers,
					'Content-Type': 'application/json',
					'X-Treams-Workspace': workspace.id,
				},
			});

			return forward(operation);
		});

		client.current = new ApolloClient({
			cache: new InMemoryCache({
				typePolicies: {
					Query: {
						fields: {
							// Cache redirect: answer batched asset lookups from the
							// normalized Asset entities, so a batch whose assets are all
							// known never hits the network (asset URLs are stable for the
							// whole browser session).
							assets: {
								read(_, { args, toReference, canRead }) {
									const refs = (args?.ids as string[] | undefined)?.map((id) =>
										toReference({ __typename: 'Asset', id }),
									);

									// Serve from cache only when every asset is present;
									// otherwise fall through to the network for the batch.
									return refs?.every(canRead) ? refs : undefined;
								},
							},
						},
					},
					EvaluationSubmissionResult: {
						merge(existing, incoming) {
							return { ...existing, ...incoming };
						},
					},
					JobPosition: {
						fields: {
							appraisal: {
								merge: mergeCompetencyAppraisals,
							},
						},
					},
					GoalConnection: {
						merge: true,
					},
				},
			}),
			link: concat(authMiddleware, httpLink),
			assumeImmutableResults: true,
			defaultOptions: {
				watchQuery: {
					fetchPolicy: 'cache-and-network',
				},
			},
		});
	}

	return <BaseApolloProvider client={client.current}>{children}</BaseApolloProvider>;
};
